export const languageNames = {
  ar: "العربية",
  nl: "Nederlands",
  en: "English",
  de: "Deutsch",
  pl: "Polski",
  fr: "Français",
  es: "Español",
  tr: "Türkçe",
  ro: "Română",
  bg: "Български",
  cs: "Čeština",
  uk: "Українська",
  hu: "Magyar",
  sk: "Slovenčina",
  ru: "Русский",
  pt: "Português",
  it: "Italiano",
};

export const countryNames = {
  ar: "المملكة العربية السعودية",
  nl: "Nederland",
  en: "United Kingdom",
  de: "Deutschland",
  pl: "Polska",
  fr: "France",
  es: "España",
  tr: "Türkiye",
  ro: "România",
  bg: "България",
  cs: "Česká republika",
  uk: "Україна",
  hu: "Magyarország",
  sk: "Slovensko",
  ru: "Россия",
  pt: "Portugal",
  it: "Italia",
};

/** Region whose flag and name stand for a locale in a country select. */
const localeRegions: Record<string, string> = {
  ar: "SA",
  nl: "NL",
  en: "GB",
  de: "DE",
  pl: "PL",
  fr: "FR",
  es: "ES",
  tr: "TR",
  ro: "RO",
  bg: "BG",
  cs: "CZ",
  uk: "UA",
  hu: "HU",
  sk: "SK",
  ru: "RU",
  pt: "PT",
  it: "IT",
};

let englishLanguageDisplay: Intl.DisplayNames | null = null;
let englishRegionDisplay: Intl.DisplayNames | null = null;

/** The English name of a language ("Arabic" for `ar`), or null when the runtime does not know the code. */
export function englishLanguageName(locale: string): string | null {
  englishLanguageDisplay ??= new Intl.DisplayNames(["en"], { type: "language" });
  try {
    return englishLanguageDisplay.of(locale) ?? null;
  } catch {
    return null;
  }
}

/** The English name of the country a locale stands for ("Saudi Arabia" for `ar`). */
export function englishCountryName(locale: string): string | null {
  const region = localeRegions[locale];
  if (!region) {
    return null;
  }
  englishRegionDisplay ??= new Intl.DisplayNames(["en"], { type: "region" });
  try {
    return englishRegionDisplay.of(region) ?? null;
  } catch {
    return null;
  }
}

/**
 * The small grey line above a language or country name in a select: its English name. Null when it adds nothing,
 * which is when the English name is the name itself ("English").
 */
export function englishSecondaryName(locale: string, nativeName: string, countries = false): string | null {
  const english = countries ? englishCountryName(locale) : englishLanguageName(locale);
  if (english === null || english.toLocaleLowerCase("en") === nativeName.toLocaleLowerCase("en")) {
    return null;
  }
  return english;
}

/**
 * Languages content can be kept in: the API's `app.enabled_locales`. The interface itself also speaks `ar`, which
 * the API does not translate content into.
 */
export const CONTENT_LOCALES = [
  "nl",
  "en",
  "de",
  "fr",
  "uk",
  "bg",
  "cs",
  "es",
  "pl",
  "ro",
  "tr",
  "hu",
  "sk",
  "ru",
  "pt",
  "it",
] as const;

export function isContentLocale(locale: string): locale is typeof CONTENT_LOCALES[number] {
  return (CONTENT_LOCALES as readonly string[]).includes(locale);
}

/** A group of locales in a locale dropdown, such as the languages that are switched on. */
export interface LocaleGroup {
  /** Header of the group in the dropdown, e.g. "Aan in Talen". */
  label: string
  /** Locales of the group in the order they are shown; locales that are not available are left out. */
  locales: string[]
}

/**
 * A language is shown with a country's flag. Most locale codes match their flag file, but "ar" (Arabic) would be
 * Argentina's flag, so it gets Saudi Arabia's.
 */
const LANGUAGE_FLAGS: Record<string, string> = { ar: "SA" };

/**
 * The flag of a language, locale or country, the same everywhere (rule of 14 Oct): 18x12 with a 2px radius and a
 * thin edge, so a white flag still shows on a white field.
 */
export const FLAG_IMAGE_CLASS = "h-3 w-[18px] shrink-0 rounded-[2px] object-cover ring-1 ring-black/8";

/** The flag image of a language, e.g. `/flags/NL.svg` for "nl". */
export function languageFlagSrc(locale: string): string {
  return `/flags/${(LANGUAGE_FLAGS[locale] ?? locale).toUpperCase()}.svg`;
}
